import { useState } from 'react'
import { MessageSquarePlus, X, Bug, Lightbulb, AlertCircle, Send } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../services/api'

type FeedbackType = 'Bug' | 'QualityIssue' | 'Suggestion' | 'CitationError' | 'Other'

const feedbackTypes: { value: FeedbackType; label: string; icon: React.ElementType }[] = [
  { value: 'Bug', label: 'Błąd', icon: Bug },
  { value: 'QualityIssue', label: 'Jakość odpowiedzi', icon: AlertCircle },
  { value: 'Suggestion', label: 'Sugestia', icon: Lightbulb },
  { value: 'CitationError', label: 'Błędna cytacja', icon: AlertCircle },
]

export default function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [type, setType] = useState<FeedbackType>('Bug')
  const [description, setDescription] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submitMutation = useMutation({
    mutationFn: (data: { type: FeedbackType; description: string }) =>
      api.post('/feedback', {
        type: data.type,
        description: data.description,
        context: {
          url: window.location.href,
        },
      }),
    onSuccess: () => {
      setSubmitted(true)
      setError(null)
      setTimeout(() => {
        setIsOpen(false)
        setSubmitted(false)
        setDescription('')
      }, 2000)
    },
    onError: () => {
      setError('Nie udalo sie wyslac zgloszenia. Sprobuj ponownie.')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (description.trim()) {
      submitMutation.mutate({ type, description })
    }
  }

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 p-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
        title="Zgłoś problem lub sugestię"
      >
        <MessageSquarePlus className="w-6 h-6" />
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Zgłoś problem lub sugestię</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {submitted ? (
              <div className="p-8 text-center">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Send className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-gray-700">Dziękujemy za zgłoszenie!</p>
                <p className="text-sm text-gray-500 mt-1">Zostanie przeanalizowane przez nasz zespół.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {/* Type selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Typ zgłoszenia
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {feedbackTypes.map((ft) => (
                      <button
                        key={ft.value}
                        type="button"
                        onClick={() => setType(ft.value)}
                        className={`flex items-center p-3 rounded-lg border text-sm transition-colors ${
                          type === ft.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <ft.icon className="w-4 h-4 mr-2" />
                        {ft.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opis
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Opisz problem lub sugestię..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                    required
                  />
                </div>

                {/* Error message */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                {/* Submit */}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="btn-secondary"
                  >
                    Anuluj
                  </button>
                  <button
                    type="submit"
                    disabled={submitMutation.isPending || !description.trim()}
                    className="btn-primary"
                  >
                    {submitMutation.isPending ? (
                      <span className="flex items-center">
                        <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full spinner" />
                        Wysyłanie...
                      </span>
                    ) : (
                      'Wyślij'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  )
}
